import { HoverCardWithLink } from '@/components/hover-card-with-link';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { Box, Card, Heading, HStack, Image, Stack, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { ILottie, LottieReact } from '@lottielab/lottie-player';
import { PropsWithChildren, ReactElement, ReactNode, RefObject } from 'react';
import { BadgeContent, TBadgeContent } from './BadgeContent';

export const VaultCard = ({
  title,
  name,
  description,
  apy,
  apyLoading,
  tvl,
  icon,
  logos = [],
  href,
  lottie,
  lottieRef,
  slotRight,
  badgeContent,
  altColor,
}: VaultCardProps) => {
  return (
    <HoverCardWithLink
      href={href}
      showHoverIcon={false}
      linkProps={{
        onClick: () =>
          trackAmplitudeEvent(AmplitudeTrackingEvent.PortalClickedHomeCard, {
            card_name: title,
          }),
      }}
      cardRootStyles={{
        border: 'none',
        h: 'full',
        onMouseEnter: () => {
          lottieRef.current?.play();
        },
        onMouseLeave: () => {
          lottieRef.current?.pause();
        },
      }}
    >
      <WithLottie ref={lottieRef} lottie={lottie} alt={<Box w="full" h="full" bgColor={altColor} />}>
        <Stack h="full" w="full" gap={8} flexDirection={['column', 'column', 'row']}>
          <VStack flex={1} gap={4} h="full" w="full" overflow={'hidden'}>
            <Card.Root border="none" borderRadius="2xl" flex={1} w="full">
              <VStack p={[6, 6, 8]} fontWeight="500" alignItems="start" gap={4} h="full">
                <Image src={icon} boxSize={12} />
                {/* Title and subtitle */}
                <Stack gap={2}>
                  {name && (
                    <>
                      <Text color="text.secondary" fontWeight="500" hideFrom="md">
                        {name}
                      </Text>
                      <Heading size="xl" fontWeight="500" color="fg.muted" hideBelow="md">
                        {name}
                      </Heading>
                    </>
                  )}

                  <Heading size={['xl', 'xl', '2xl']} fontWeight="700">
                    {title}
                  </Heading>
                  <Text color="text.secondary" fontWeight="500" hideFrom="md">
                    {description}
                  </Text>
                  <Heading size="lg" color="fg.muted" fontWeight="500" lineHeight="24px" hideBelow="md">
                    {description}
                  </Heading>
                </Stack>

                {/* Badges and Stats Row */}
                <BadgeContent badgeContent={badgeContent} apy={apy} tvl={tvl} apyLoading={apyLoading} />
              </VStack>
            </Card.Root>
            {/* Logos or Vault Buttons */}
            {logos?.length ? (
              <HStack gap={4} w="full">
                {logos.map((srcOrLabel, i) => (
                  <Card.Root key={srcOrLabel} h="93px" border="none" borderRadius="xl" flex={1}>
                    <Card.Body display="flex" justifyContent="center" alignItems={'center'}>
                      <Image key={i} src={srcOrLabel} objectFit="contain" />
                    </Card.Body>
                  </Card.Root>
                ))}
              </HStack>
            ) : null}
          </VStack>

          {slotRight ? (
            <Card.Root
              flex={1}
              borderRadius="2xl"
              overflow="hidden"
              position="relative"
              h="full"
              w="full"
              minH={['300px', '600px', 'initial']}
              display={['block', 'block', 'none', 'none', 'block']}
            >
              {slotRight}
            </Card.Root>
          ) : null}
        </Stack>
      </WithLottie>
    </HoverCardWithLink>
  );
};

export const CtaCard = ({
  title,
  subtitle,
  description,
  icon,
  href,
  lottie,
  lottieRef,
  lottieContent,
  lottieContentRef,
  children,
  altColor,
}: {
  title: string;
  subtitle?: string;
  description: string;
  icon: string;
  href?: string;
  lottie: string;
  lottieRef: RefObject<ILottie | null>;
  altColor: string;
  lottieContent?: string;
  lottieContentRef?: RefObject<ILottie | null>;
} & PropsWithChildren) => {
  return (
    <HoverCardWithLink
      href={href}
      cardRootStyles={{
        border: 'none',
        h: 'full',
        onMouseEnter: () => {
          lottieRef.current?.play();
        },
        onMouseLeave: () => {
          lottieRef.current?.pause();
        },
      }}
      showHoverIcon={false}
    >
      <WithLottie lottie={lottie} ref={lottieRef} alt={<Box h="full" w="full" bgColor={altColor} />}>
        <Card.Root
          borderRadius="2xl"
          border="none"
          h="full"
          pb={{
            base: '40px',
            lg: 0,
          }}
        >
          <Card.Body p={[6, 6, 8]}>
            <VStack gap={3} justifyContent="center" h="full" textAlign="center">
              <Image src={icon} height="56px" />
              <>
                <Heading size="2xl" fontWeight="700" hideBelow="md">
                  {title}
                </Heading>
              </>
              {subtitle && (
                <Heading w="80%" size="lg" fontWeight="500">
                  {subtitle}
                </Heading>
              )}
              <Heading mb={4} w="80%" size="lg" color="fg.muted" fontWeight="500" hideBelow="md">
                {description}
              </Heading>
              <Text color="text.secondary" fontWeight="500" hideFrom="md">
                {description}
              </Text>
              {children}
            </VStack>
          </Card.Body>
          {lottieContent && (
            <Box
              mb={{
                base: 0,
                lg: '80px',
              }}
              mt={{
                base: 0,
                lg: '-40px',
              }}
            >
              <LottieReact src={lottieContent} ref={lottieContentRef} autoplay={true} loop style={{ width: '100%' }} />
            </Box>
          )}
        </Card.Root>
      </WithLottie>
    </HoverCardWithLink>
  );
};

export const WithLottie = ({
  children,
  lottie,
  ref,
  alt,
}: PropsWithChildren & { lottie: string; ref: RefObject<ILottie | null>; alt: ReactElement }) => {
  const shouldShowLottie = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
  });
  return (
    <Card.Root position="relative" overflow="hidden" border="none" h="full">
      <Card.Body p={[4, 4, 8]} h="full" w="full">
        {/* Background Lottie */}
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          zIndex={0}
          pointerEvents="none" // prevent it from blocking interactivity
        >
          {shouldShowLottie ? (
            <LottieReact
              src={lottie}
              ref={ref}
              autoplay={false}
              loop
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              preserveAspectRatio="xMidYMid slice"
            />
          ) : (
            alt
          )}
        </Box>

        {/* Foreground content */}
        <Box position="relative" zIndex={1} h="full" w="full">
          {children}
        </Box>
      </Card.Body>
    </Card.Root>
  );
};
export type VaultCardProps = {
  title: string;
  name: string;
  description: string;
  apy?: string | number | undefined;
  apyLoading?: boolean;
  icon: string;
  tvl?: string;
  logos?: string[]; // logo image srcs or vault icons
  href: string;
  lottie: string;
  lottieRef: RefObject<ILottie | null>;
  slotRight?: ReactNode;
  badgeContent: TBadgeContent;
  altColor: string;
};
